/**
 * NotificationSound — plays a soft ping when a new agent message arrives.
 *
 * Default: the bundled notification-sound.mp3 (inlined as base64 at build time).
 * Falls back to a Web Audio API synthetic chime if the file was not bundled.
 * Override: pass `soundUrl` (MP3/OGG/WAV URL or base64 data URI) to use a different file.
 */
import BUNDLED_SOUND from './notificationSoundData.js';

export class NotificationSound {
	constructor({ soundUrl = BUNDLED_SOUND, volume = 0.4 } = {}) {
		this._soundUrl = soundUrl;
		this._volume = Math.min(1, Math.max(0, volume));
		this._enabled = true;
		this._audioCtx = null;
		this._audioBuffer = null;
		this._loadPromise = null;

		if (soundUrl) {
			this._loadPromise = this._loadAudioFile(soundUrl);
		}
	}

	setEnabled(enabled) {
		this._enabled = Boolean(enabled);
	}

	setVolume(volume) {
		this._volume = Math.min(1, Math.max(0, volume));
	}

	async play() {
		if (!this._enabled) return;
		if (typeof window === 'undefined') return;

		try {
			if (this._soundUrl) {
				await this._playFile();
			} else {
				await this._playSynthetic();
			}
		} catch {
			/* autoplay policy or context suspended — silent fail */
		}
	}

	// ── Private ────────────────────────────────────────────────────────────────

	_getAudioContext() {
		if (!this._audioCtx || this._audioCtx.state === 'closed') {
			const AudioContext = window.AudioContext || window.webkitAudioContext;
			if (!AudioContext) return null;
			this._audioCtx = new AudioContext();
		}
		return this._audioCtx;
	}

	/**
	 * Synthetic two-tone chime via Web Audio API — no file required.
	 *  • First tone:  880 Hz, 0–80 ms
	 *  • Second tone: 1100 Hz, 60–160 ms
	 * Sounds like a gentle message ping.
	 */
	async _playSynthetic() {
		const ctx = this._getAudioContext();
		if (!ctx) return;

		if (ctx.state === 'suspended') {
			await ctx.resume();
		}

		const now = ctx.currentTime;
		const vol = this._volume;

		const tones = [
			{ freq: 880, start: 0, end: 0.08 },
			{ freq: 1100, start: 0.06, end: 0.16 },
		];

		tones.forEach(({ freq, start, end }) => {
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();

			osc.type = 'sine';
			osc.frequency.value = freq;

			gain.gain.setValueAtTime(0, now + start);
			gain.gain.linearRampToValueAtTime(vol, now + start + 0.01);
			gain.gain.linearRampToValueAtTime(0, now + end);

			osc.connect(gain);
			gain.connect(ctx.destination);

			osc.start(now + start);
			osc.stop(now + end + 0.01);
		});
	}

	async _loadAudioFile(url) {
		try {
			const ctx = this._getAudioContext();
			if (!ctx) return;

			const response = await fetch(url);
			const arrayBuffer = await response.arrayBuffer();
			this._audioBuffer = await ctx.decodeAudioData(arrayBuffer);
		} catch {
			/* failed to load — fall back to synthetic */
			this._soundUrl = null;
		}
	}

	async _playFile() {
		if (this._loadPromise) {
			await this._loadPromise;
			this._loadPromise = null;
		}

		if (!this._audioBuffer) {
			this._playSynthetic();
			return;
		}

		const ctx = this._getAudioContext();
		if (!ctx) return;

		if (ctx.state === 'suspended') {
			await ctx.resume();
		}

		const source = ctx.createBufferSource();
		const gain = ctx.createGain();

		source.buffer = this._audioBuffer;
		gain.gain.value = this._volume;

		source.connect(gain);
		gain.connect(ctx.destination);
		source.start();
	}
}
